import { Component, Input } from '@angular/core';
import { AuthorizationModule } from '@app/shared/directives/authorization.module';
import { IsFeatureAvailableDirective } from '@app/shared/directives/is-feature-available.directive';
import { MatOption } from '@angular/material/autocomplete';
import { MatSelect } from '@angular/material/select';
import { PanelComponent } from '@app/beacons/configuration/panel/panel.component';
import { PanelFooterComponent } from '@app/beacons/configuration/panel-footer/panel-footer.component';
import { ReactiveFormsModule } from '@angular/forms';
import { SharedComponentsModule } from '@app/shared/shared-components.module';
import { MatSlider, MatSliderThumb } from '@angular/material/slider';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { LightingConfigurationService } from '@services/lighting-configuration.service';
import { SensorNode } from '@app/shared/models/sensor-node';
import { ToastService } from '@services/toast/toast.service';
import { LightingConfiguration } from '@app/shared/models/lighting-configuration';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { SensorNodeStatus } from '@app/shared/models/sensor-node-status';
import { FeatureService } from '@services/feature.service';
import { forkJoin, Observable, take } from 'rxjs';

type LightingConfig =
  | 'zone'
  | 'maxLight'
  | 'lowLight'
  | 'minLight'
  | 'dwellTime'
  | 'lowLightTime'
  | 'scene'
  | 'motionSensitivity'
  | 'motionIdleMode'
  | 'personality';

@Component({
  selector: 'app-lighting-config-panel',
  standalone: true,
  imports: [
    AuthorizationModule,
    IsFeatureAvailableDirective,
    MatOption,
    MatSelect,
    PanelComponent,
    PanelFooterComponent,
    ReactiveFormsModule,
    SharedComponentsModule,
    MatSlider,
    MatSliderThumb,
    MatSlideToggle
  ],
  providers: [{ provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'outline' } }],
  templateUrl: './lighting-config-panel.component.html',
  styleUrl: './lighting-config-panel.component.scss'
})
export class LightingConfigPanelComponent {
  @Input() buildingId: number;
  @Input() floorId!: number;
  @Input() isReady: boolean;
  @Input() set selectedNode(node: SensorNode) {
    this.setNodeConfigValues(node);
  }
  get selectedNode(): SensorNode {
    return this._selectedNode;
  }
  private _selectedNode: SensorNode;
  isBusy = false;

  zone = -1;
  private originalZone: number;

  indexedMaxLight = 0;
  originalMaxLight: number;

  indexedLowLight = 0;
  private originalLowLight: number;

  indexedMinLight = 0;
  private originalMinLight: number;

  dwellTime: number;
  private originalDwellTime: number;

  lowLightTime: number;
  private originalLowLightTime: number;

  scene: number;
  private originalScene: number;

  motionSensitivity = 0;
  private originalMotionSensitivity: number;

  get motionSensitivityIndex(): number {
    return this.lightingConfigService.getMotionSensitivityIndex(this.motionSensitivity);
  }

  set motionSensitivityIndex(index: number) {
    this.motionSensitivity = this.lightingConfigService.getMotionSensitivityValue(index);
  }

  motionIdleMode = false;
  private originalMotionIdleMode: boolean;

  personality = 0;
  private originalPersonality: number;

  constructor(
    private readonly toastService: ToastService,
    protected readonly lightingConfigService: LightingConfigurationService,
    private readonly featureService: FeatureService
  ) {}

  setNodeConfigValues(node: SensorNode): void {
    this._selectedNode = node;
    if (node?.lightingConfiguration) {
      const config = node.lightingConfiguration;
      this.zone = config.zone;
      this.indexedMaxLight = config.maxLightLevel;
      this.indexedLowLight = config.lowLightLevel;
      this.indexedMinLight = config.minLightLevel;
      this.dwellTime = config.dwellTime;
      this.lowLightTime = config.lowLightTime;
      this.motionSensitivity = config.motionSensitivity ?? 0;
      this.motionIdleMode = config.motionIdleMode ?? false;
      this.personality = config.personality ?? 0;
    } else {
      this.zone = -1;
      this.indexedMaxLight = this.lightingConfigService.DEFAULT_MAX_LIGHT;
      this.indexedLowLight = this.lightingConfigService.DEFAULT_LOW_LIGHT;
      this.indexedMinLight = this.lightingConfigService.DEFAULT_MIN_LIGHT;
      this.dwellTime = -1;
      this.lowLightTime = -1;
      this.motionSensitivity = 0;
      this.motionIdleMode = false;
      this.personality = 0;
    }

    this.scene = this.getScene(node);
    this.originalZone = this.zone;
    this.originalMaxLight = this.indexedMaxLight;
    this.originalLowLight = this.indexedLowLight;
    this.originalMinLight = this.indexedMinLight;
    this.originalDwellTime = this.dwellTime;
    this.originalLowLightTime = this.lowLightTime;
    this.originalScene = this.scene;
    this.originalMotionSensitivity = this.motionSensitivity;
    this.originalMotionIdleMode = this.motionIdleMode;
    this.originalPersonality = this.personality;
  }

  private getScene(node: SensorNode): number {
    return node?.lightingConfiguration?.scene ?? node?.scene ?? -1;
  }

  /**
   * Runs a config update request, handling isBusy toggling and success/error toasts consistently.
   */
  private runUpdate<T>(config: {
    request: () => Observable<T>;
    onSuccess: (result: T) => void;
    successMessage: (result: T) => string;
    successDataCy: string;
    errorMessage: string;
    errorDataCy: string;
  }): void {
    this.isBusy = true;
    config.request().subscribe({
      next: (result) => {
        config.onSuccess(result);
        this.isBusy = false;
        this.toastService.success({ message: config.successMessage(result), dataCy: config.successDataCy });
      },
      error: (err) => {
        console.error(err);
        this.toastService.error({ message: config.errorMessage, dataCy: config.errorDataCy });
        this.isBusy = false;
      }
    });
  }

  setZone(): void {
    if (this._selectedNode) {
      this.runUpdate({
        request: () => this.lightingConfigService.updateZone(this._selectedNode, this.zone, this.buildingId),
        onSuccess: () => (this.originalZone = this.zone),
        successMessage: () => `The lighting configuration zone has been set to ${this.zoneValue}`,
        successDataCy: 'zone-updated-toast',
        errorMessage: 'Failed to update zone',
        errorDataCy: 'failed-to-update-zone-toast'
      });
    }
  }

  resetZone(): void {
    this.zone = this.originalZone;
  }

  get zoneValue(): string {
    return this.lightingConfigService.convertZoneIdToName(this.zone);
  }

  setMaxLight(): void {
    if (this._selectedNode) {
      this.runUpdate({
        request: () => this.lightingConfigService.updateMaxLight(this._selectedNode, this.indexedMaxLight, this.buildingId),
        onSuccess: () => (this.originalMaxLight = this.indexedMaxLight),
        successMessage: () => `The lighting configuration Max Light level has been set to ${this.maxLight}`,
        successDataCy: 'max-light-updated-toast',
        errorMessage: 'Failed to update Max light',
        errorDataCy: 'failed-to-update-max-light-toast'
      });
    }
  }

  resetMaxLight(): void {
    this.indexedMaxLight = this.originalMaxLight;
  }

  get maxLight(): string {
    return this.lightingConfigService.convertMaxLightIdToName(this.indexedMaxLight);
  }

  setLowLight(): void {
    if (this._selectedNode) {
      this.runUpdate({
        request: () => this.lightingConfigService.updateLowLight(this._selectedNode, this.indexedLowLight, this.buildingId),
        onSuccess: () => (this.originalLowLight = this.indexedLowLight),
        successMessage: () => `The lighting configuration Low Light level has been set to ${this.lowLight}`,
        successDataCy: 'low-light-updated-toast',
        errorMessage: 'Failed to update Low light',
        errorDataCy: 'failed-to-update-low-light-toast'
      });
    }
  }

  resetLowLight(): void {
    this.indexedLowLight = this.originalLowLight;
  }

  get lowLight(): string {
    return this.lightingConfigService.convertLowLightIdToName(this.indexedLowLight);
  }

  setMinLight(): void {
    if (this._selectedNode) {
      this.runUpdate({
        request: () => this.lightingConfigService.updateMinLight(this._selectedNode, this.indexedMinLight, this.buildingId),
        onSuccess: () => (this.originalMinLight = this.indexedMinLight),
        successMessage: () => `The lighting configuration Min Light level has been set to ${this.minLight}`,
        successDataCy: 'min-light-updated-toast',
        errorMessage: 'Failed to update Min light',
        errorDataCy: 'failed-to-update-min-light-toast'
      });
    }
  }

  resetMinLight(): void {
    this.indexedMinLight = this.originalMinLight;
  }

  get minLight(): string {
    return this.lightingConfigService.convertMinLightIdToName(this.indexedMinLight);
  }

  setDwellTime(): void {
    if (this._selectedNode) {
      this.runUpdate({
        request: () => this.lightingConfigService.updateDwellTime(this._selectedNode, this.dwellTime, this.buildingId),
        onSuccess: () => (this.originalDwellTime = this.dwellTime),
        successMessage: () => `The lighting configuration Dwell Light Time has been set to ${this.dwellTimeValue}`,
        successDataCy: 'dwell-time-updated-toast',
        errorMessage: 'Failed to update dwell time',
        errorDataCy: 'failed-to-update-dwell-time-toast'
      });
    }
  }

  resetDwellTime(): void {
    this.dwellTime = this.originalDwellTime;
  }

  get dwellTimeValue(): string {
    return this.lightingConfigService.convertTimeIdToName(this.dwellTime);
  }

  setLowLightTime(): void {
    if (this._selectedNode) {
      this.runUpdate({
        request: () => this.lightingConfigService.updateLowLightTime(this._selectedNode, this.lowLightTime, this.buildingId),
        onSuccess: () => (this.originalLowLightTime = this.lowLightTime),
        successMessage: () => `The lighting configuration Low Light Time has been set to ${this.lowLightTimeValue}`,
        successDataCy: 'low-light-time-updated-toast',
        errorMessage: 'Failed to update low light time',
        errorDataCy: 'failed-to-update-low-light-time-toast'
      });
    }
  }

  resetLowLightTime(): void {
    this.lowLightTime = this.originalLowLightTime;
  }

  get lowLightTimeValue(): string {
    return this.lightingConfigService.convertTimeIdToName(this.lowLightTime);
  }

  setScene(): void {
    if (this._selectedNode) {
      this.runUpdate({
        request: () => this.lightingConfigService.updateScene(this._selectedNode, this.scene, this.buildingId),
        onSuccess: () => (this.originalScene = this.scene),
        successMessage: () => `The lighting configuration scene has been set to ${this.sceneValue}`,
        successDataCy: 'scene-updated-toast',
        errorMessage: 'Failed to update scene',
        errorDataCy: 'failed-to-update-scene-toast'
      });
    }
  }

  get isZoneSupported(): boolean {
    return LightingConfiguration.isZoneSupportedNode(this._selectedNode?.nodeType);
  }

  get isMotionSupported(): boolean {
    return LightingConfiguration.isMotionSupportedNode(this._selectedNode?.nodeType);
  }

  get sceneValue(): string {
    return this.lightingConfigService.convertSceneIdToName(this.scene);
  }

  resetScene(): void {
    this.scene = this.originalScene;
  }

  setMotionSensitivity(): void {
    if (this._selectedNode) {
      this.runUpdate({
        request: () => forkJoin([
          this.featureService
            .isAvailable(
              'motion-sensitivity-old-ui',
              this._selectedNode.nodeType,
              this._selectedNode.picVersion,
              this._selectedNode.nordicVersion
            )
            .pipe(take(1)),
          this.lightingConfigService.updateMotionSensitivity(this._selectedNode, this.motionSensitivity, this.buildingId)
        ]),
        onSuccess: () => (this.originalMotionSensitivity = this.motionSensitivity),
        successMessage: ([isOldUi, _update]) => {
          const label = isOldUi
            ? this.lightingConfigService.convertOldMotionSensitivityToLabel(this.motionSensitivity)
            : this.motionSensitivityLabel;
          return `The motion sensitivity has been set to ${label}`;
        },
        successDataCy: 'motion-sensitivity-updated-toast',
        errorMessage: 'Failed to update motion sensitivity',
        errorDataCy: 'failed-to-update-motion-sensitivity-toast'
      });
    }
  }

  resetMotionSensitivity(): void {
    this.motionSensitivity = this.originalMotionSensitivity;
  }

  get motionSensitivityLabel(): string {
    return this.lightingConfigService.convertMotionSensitivityToLabel(this.motionSensitivity);
  }

  setMotionIdleMode(): void {
    if (this._selectedNode) {
      this.runUpdate({
        request: () => this.lightingConfigService.updateMotionIdleMode(this._selectedNode, this.motionIdleMode, this.buildingId),
        onSuccess: () => (this.originalMotionIdleMode = this.motionIdleMode),
        successMessage: () => `Lower Sensitivity when in Standby has been ${this.motionIdleMode ? 'enabled' : 'disabled'}`,
        successDataCy: 'motion-idle-mode-updated-toast',
        errorMessage: 'Failed to update Lower Sensitivity when in Standby',
        errorDataCy: 'failed-to-update-motion-idle-mode-toast'
      });
    }
  }

  resetMotionIdleMode(): void {
    this.motionIdleMode = this.originalMotionIdleMode;
  }

  setPersonality(): void {
    if (this._selectedNode) {
      this.runUpdate({
        request: () => this.lightingConfigService.updatePersonality(this._selectedNode, this.personality, this.buildingId),
        onSuccess: () => (this.originalPersonality = this.personality),
        successMessage: () => `The personality has been set to ${this.personalityLabel}`,
        successDataCy: 'personality-updated-toast',
        errorMessage: 'Failed to update personality',
        errorDataCy: 'failed-to-update-personality-toast'
      });
    }
  }

  resetPersonality(): void {
    this.personality = this.originalPersonality;
  }

  get personalityLabel(): string {
    return this.lightingConfigService.convertPersonalityIdToName(this.personality);
  }

  tooltip(config?: LightingConfig): string {
    if (this._selectedNode?.status === SensorNodeStatus.DISCONNECTED) {
      return 'Node is disconnected';
    }

    if (!this.isReady) {
      return this._selectedNode ? 'Please wait for the configuration to load' : 'Please select a node';
    }

    if (!config) {
      return '';
    }

    switch (config) {
      case 'zone':
        return this.getZoneTooltip();
      case 'maxLight':
        return this.getMaxLightTooltip();
      case 'lowLight':
        return this.getLowLightTooltip();
      case 'minLight':
        return this.getMinLightTooltip();
      case 'dwellTime':
        return this.getDwellTimeTooltip();
      case 'lowLightTime':
        return this.getLowLightTimeTooltip();
      case 'scene':
        return this.getSceneTooltip();
      case 'motionSensitivity':
        return this.getMotionSensitivityTooltip();
      case 'motionIdleMode':
        return this.getMotionIdleModeTooltip();
      case 'personality':
        return this.getPersonalityTooltip();
      default:
        return '';
    }
  }

  private getZoneTooltip(): string {
    return 'Zones are used to group nodes together, nodes in the same zone will share occupancy messages and turn on together depending on their Personality';
  }

  private getMaxLightTooltip(): string {
    return 'Max light sets the Light output when presence is detected while in Auto mode';
  }

  private getLowLightTooltip(): string {
    return 'Low light sets the Light output when no presence has been detected for "Dwell Time" while in Auto mode';
  }

  private getMinLightTooltip(): string {
    return 'Min light sets the Light output when no presence has been detected for "Dwell Time" + "Low Light Time" while in Auto mode';
  }

  private getDwellTimeTooltip(): string {
    return 'Dwell time is the amount of time to stay at "Max light" for without presence';
  }

  private getLowLightTimeTooltip(): string {
    return 'Low light time is the amount of time to stay at "Low light" for without presence';
  }

  private getSceneTooltip(): string {
    return 'Go to the Scene selected, Scenes can be configured for different Light outputs, Colour temperatures and the amount of time the node should stay in that Scene for, before returning to Auto mode';
  }

  private getMotionSensitivityTooltip(): string {
    return 'Motion sensitivity is the amount of movement required to trigger a motion event, the higher the sensitivity the less movement is required to trigger a motion event';
  }

  private getMotionIdleModeTooltip(): string {
    return 'Lower sensitivity when in Standby mode is used to prevent false motion events from causing the light to turn on, the node will require more movement to trigger motion events when in Standby mode (Off Luminaire). When the Luminaire is on, the sensitivity will be increased to the configured "Motion Sensitivity" value so less motion is required to keep the Luminaire on';
  }

  private getPersonalityTooltip(): string {
    return 'Personalities determine how nodes react to motion events, determining how far away from the motion event the luminaire comes on and to what level';
  }
}
